import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { User, Lock, Loader2 } from "lucide-react";

/**
 * Admin Login — split-screen enterprise design.
 *
 *   ┌─────────────────┬──────────────────┐
 *   │                 │                  │
 *   │  Image (left)   │  Login form      │
 *   │  50% on md+     │  50% on md+      │
 *   │                 │                  │
 *   └─────────────────┴──────────────────┘
 *
 * Left pane: proposal_bg.jpg with a soft white wash overlay.
 * Right pane: clean white form with Inter typography, soft gray inputs,
 *            a coral→pink gradient CTA. No "Don't have an account?",
 *            "Request Access", or "Forgot password?".
 *
 * The credential field is intentionally labelled "Username" rather than
 * "Email" — the form still accepts an email, but obscuring the field
 * type makes it harder for an intruder to know what the system expects
 * (a security-through-obscurity tactic, complemented by a generic
 * "username not recognised" error on failure).
 */
export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(username, password);
      navigate("/");
    } catch (_err) {
      // Generic error message — never confirm whether the username or the
      // password was the wrong field.
      setError("Username or password not recognised. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-shell">
      {/* ── Left: image / placeholder area ─────────────────────── */}
      <div className="login-image-pane" aria-hidden />

      {/* ── Right: login form ─────────────────────────────────── */}
      <div className="login-form-pane">
        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {/* Brand mark — uses the actual MerryFlix logo */}
          <div className="login-brand">
            <img
              src="/merryflix_logo.png"
              alt="MerryFlix"
              className="login-brand-logo"
            />
            <div>
              <div className="login-brand-name">MerryFlix</div>
              <div className="login-brand-sub">Admin Portal</div>
            </div>
          </div>

          {/* Headline */}
          <div>
            <h1 className="login-headline">Welcome</h1>
          </div>

          {/* Error banner (only on login failure) — always refers to "username" */}
          {error && <div className="login-error" role="alert">{error}</div>}

          {/* Username (deliberately labelled, accepts email under the hood) */}
          <div className="login-field">
            <label className="login-label" htmlFor="login-username">Username</label>
            <div className="relative">
              <User
                size={22}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <input
                id="login-username"
                type="text"
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username"
                required
                className="login-input login-input--with-icon"
              />
            </div>
          </div>

          {/* Password */}
          <div className="login-field">
            <label className="login-label" htmlFor="login-password">Password</label>
            <div className="relative">
              <Lock
                size={22}
                className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400"
              />
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="login-input login-input--with-icon tracking-widest"
              />
            </div>
          </div>

          {/* CTA */}
          <button
            type="submit"
            className="login-cta"
            disabled={loading}
            aria-label={loading ? "Signing in" : "Sign in"}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 size={22} className="animate-spin" />
                Signing in…
              </span>
            ) : (
              "Login"
            )}
          </button>

          {/* Subhead — moved to below the button */}
          <p className="login-subhead login-subhead--below">
            Sign in to access the MerryFlix admin workspace.
          </p>
        </form>
      </div>
    </div>
  );
}